import { z } from 'zod';
import { isValidPhoneNumber, transformToE164, PHONE_VALIDATION_MESSAGE } from '../utils/phone.utils';

/**
 * Schema for password validation with comprehensive security requirements.
 * 
 * This schema enforces strong password policies by requiring:
 * - Minimum 8 characters
 * - At least one letter (uppercase or lowercase)
 * - At least one digit
 * - At least one special character (non-alphanumeric)
 * 
 * @example
 * passwordSchema.parse("SecurePass123!") // ✅ Valid
 * passwordSchema.parse("weak") // ❌ Invalid - too short, missing requirements
 * passwordSchema.parse("password123") // ❌ Invalid - missing special character
 * 
 * @returns Zod schema that validates and transforms password input
 */
export const passwordSchema = z.string()
    .trim()
    .min(8, "Min 8 chars, include letters, numbers & symbols.")
    .regex(/[a-zA-Z]/, "Min 8 chars, include letters, numbers & symbols.")
    .regex(/\d/, "Min 8 chars, include letters, numbers & symbols.")
    .regex(/[^a-zA-Z0-9]/, "Min 8 chars, include letters, numbers & symbols.")

/**
 * Schema for email validation and normalization using Zod's built-in email validator.
 * 
 * Validates that the input follows standard email format requirements:
 * - Contains @ symbol
 * - Has valid domain structure
 * - Follows RFC 5322 email standards
 * 
 * Automatically normalizes email addresses by:
 * - Converting to lowercase (email addresses are case-insensitive per RFC 5321)
 * - Trimming whitespace
 * 
 * @example
 * emailSchema.parse("user@example.com") // ✅ Valid - returns "user@example.com"
 * emailSchema.parse("USER@EXAMPLE.COM") // ✅ Valid - returns "user@example.com"
 * emailSchema.parse(" User@Example.com ") // ✅ Valid - returns "user@example.com"
 * emailSchema.parse("invalid-email") // ❌ Invalid
 * emailSchema.parse("user@") // ❌ Invalid
 * 
 * @returns Zod schema that validates and normalizes email format
 */
export const emailSchema = z.string()
    .email()
    .transform(email => email.toLowerCase().trim());

/**
 * Schema for phone number validation and normalization.
 * 
 * Accepts phone numbers in multiple formats and normalizes them to E.164 format:
 * - 10-digit US numbers (adds +1 prefix)
 * - 11-digit US numbers starting with 1 (adds + prefix)
 * - International numbers with country code (preserves as-is)
 * - Automatically cleans non-digit characters while preserving +
 * 
 * @example
 * phoneSchema.parse("1234567890") // returns "+11234567890"
 * phoneSchema.parse("+11234567890") // returns "+11234567890"
 * phoneSchema.parse("(123) 456-7890") // returns "+11234567890"
 * phoneSchema.parse("123-456-7890") // returns "+11234567890"
 * phoneSchema.parse("+44 20 1234 5678") // returns "+442012345678"
 * 
 * @returns Zod schema that validates and transforms phone numbers to E.164 format
 */
export const phoneSchema = z.string()
    .min(1, "Phone number is required")
    .refine(value => isValidPhoneNumber(value), {
        message: PHONE_VALIDATION_MESSAGE
    })
    .transform(value => transformToE164(value))

/**
 * Schema for date validation that accepts both Date objects and date strings.
 * 
 * This schema handles dates in multiple formats:
 * - JavaScript Date objects
 * - ISO date strings (YYYY-MM-DD or full ISO format)
 * - Automatically transforms strings to Date objects
 * 
 * @example
 * dateSchema.parse(new Date()) // ✅ Valid - returns Date
 * dateSchema.parse("2023-12-25") // ✅ Valid - transforms to Date
 * dateSchema.parse("2023-12-25T10:00:00Z") // ✅ Valid - transforms to Date
 * dateSchema.parse("invalid") // ❌ Invalid
 * 
 * @returns Zod schema that validates and transforms dates
 */
export const dateSchema = z.preprocess(
    (val) => {
        // If already a Date object, return as-is
        if (val instanceof Date) {
            return val;
        }
        
        // If string, parse it
        if (typeof val === 'string' && val.trim() !== '') {
            const parsed = new Date(val);
            // Return parsed date if valid, otherwise return original to trigger validation error
            return isNaN(parsed.getTime()) ? val : parsed;
        }
        
        // Return as-is for other types (will be validated by z.date())
        return val;
    },
    z.date()
)

/**
 * Predefined text size configurations for consistent validation across the application.
 * 
 * Each size defines minimum and maximum character limits:
 * - sm: 1-100 characters (short text, names, titles)
 * - md: 1-200 characters (medium text, descriptions)
 * - lg: 1-2000 characters (long text, content, comments)
 */
const textSchemaSizes = {
    sm: { min: 1, max: 100 },
    md: { min: 1, max: 200 },
    lg: { min: 1, max: 2000 },
}

/**
 * Factory function that creates text validation schemas with configurable size limits.
 * 
 * Provides consistent text validation across the application with:
 * - Automatic trimming of whitespace
 * - Configurable minimum and maximum lengths
 * - Clear error messages
 * 
 * @param size - The text size configuration to use ('sm', 'md', 'lg')
 * @returns Zod schema configured for the specified text size
 * 
 * @example
 * const shortTextSchema = textSchema('sm');
 * shortTextSchema.parse("Hello") // ✅ Valid
 * shortTextSchema.parse("") // ❌ Invalid - too short
 * shortTextSchema.parse("a".repeat(101)) // ❌ Invalid - too long
 */
export const textSchema = (size: keyof typeof textSchemaSizes = "sm") => z
    .string()
    .trim()
    .min(textSchemaSizes[size].min, "Required")
    .max(textSchemaSizes[size].max, `Cannot exceed ${textSchemaSizes[size].max} characters`);

/**
 * Factory function that creates multi-select validation schemas.
 * 
 * Validates arrays of selected values against a predefined set of options.
 * Useful for form inputs that allow multiple selections (checkboxes, multi-select dropdowns).
 * 
 * @template V - The type of the option values (must extend string)
 * @param options - Array of option objects with label and value properties
 * @returns Zod schema that validates arrays of selected option values
 * 
 * @example
 * const fruitOptions = [
 *   { label: "Apple", value: "apple" },
 *   { label: "Banana", value: "banana" },
 *   { label: "Orange", value: "orange" }
 * ] as const;
 * 
 * const fruitMultiSelectSchema = multiSelectSchema(fruitOptions);
 * fruitMultiSelectSchema.parse(["apple", "banana"]) // ✅ Valid
 * fruitMultiSelectSchema.parse(["apple", "grape"]) // ❌ Invalid - grape not in options
 */
export const multiSelectSchema = <V extends string>(
    options: readonly { label: string, value: V }[]
) => {
    const schema = z.array(
        z.enum(options.map(option => option.value) as [
            V,
            ...V[]
        ]))
    return schema
}

/**
 * Factory function that creates single-select validation schemas.
 * 
 * Validates a single selected value against a predefined set of options.
 * Useful for form inputs that allow only one selection (radio buttons, select dropdowns).
 * 
 * @template V - The type of the option values (must extend string)
 * @param options - Array of option objects with label and value properties
 * @returns Zod schema that validates a single selected option value
 * 
 * @example
 * const statusOptions = [
 *   { label: "Active", value: "active" },
 *   { label: "Inactive", value: "inactive" },
 *   { label: "Pending", value: "pending" }
 * ] as const;
 * 
 * const statusSelectSchema = selectSchema(statusOptions);
 * statusSelectSchema.parse("active") // ✅ Valid
 * statusSelectSchema.parse("unknown") // ❌ Invalid - not in options
 */
export const selectSchema = <V extends string>(
    options: readonly { label: string, value: V }[]
) => {
    const schema = z.enum(
        options.map(option => option.value) as [
            V,
            ...V[]
        ])
    return schema
}

/**
 * Schema for optional phone number validation with flexible input handling.
 * 
 * Accepts empty strings, valid phone numbers, or undefined values.
 * Automatically normalizes valid phone numbers to E.164 format.
 * Useful for forms where phone number is optional.
 * 
 * @example
 * optionalPhoneSchema.parse("") // ✅ Valid - empty string
 * optionalPhoneSchema.parse("1234567890") // ✅ Valid - returns "+11234567890"
 * optionalPhoneSchema.parse(undefined) // ✅ Valid - undefined
 * optionalPhoneSchema.parse("invalid") // ❌ Invalid
 * 
 * @returns Zod schema that accepts optional phone numbers
 */
export const optionalPhoneSchema = z.preprocess(
    (val) => {
        // Treat null, undefined, and blank strings as absent — no validation error
        if (val === null || val === undefined) return undefined;
        if (typeof val === "string" && val.trim() === "") return undefined;
        return val;
    },
    phoneSchema.optional()
)

/**
 * Schema for positive number validation.
 * 
 * Ensures the input is a number greater than or equal to 1.
 * Useful for quantities, IDs, counts, and other numeric values that must be positive.
 * 
 * @example
 * numberSchema.parse(1) // ✅ Valid
 * numberSchema.parse(100) // ✅ Valid
 * numberSchema.parse(0) // ❌ Invalid - must be >= 1
 * numberSchema.parse(-1) // ❌ Invalid - must be >= 1
 * 
 * @returns Zod schema that validates positive numbers
 */
export const numberSchema =  z.number().min(1, "Required")

/**
 * Schema for claimedBy fields that accept arrays of strings in the form.
 * 
 * This schema is used for multi-select "Claimed By" fields that store
 * multiple organization names as an array. The data is stored in the database
 * as a JSON string in a VARCHAR field, but the form works with arrays.
 * 
 * Accepts both strings (for backward compatibility with forms still using FormSelectInput)
 * and arrays (for forms using FormMultiSelectInput), and normalizes strings to arrays.
 * 
 * @example
 * claimedBySchema.parse(["Adventures Relief", "CORE"]) // ✅ Valid - returns array
 * claimedBySchema.parse([]) // ✅ Valid - empty array
 * claimedBySchema.parse(null) // ✅ Valid - null
 * claimedBySchema.parse("Adventures Relief") // ✅ Valid - converts to ["Adventures Relief"]
 * claimedBySchema.parse('["Adventures Relief", "CORE"]') // ✅ Valid - parses JSON string
 * 
 * @returns Zod schema that validates and normalizes to arrays of strings
 */
export const claimedBySchema = z.preprocess(
    (val) => {
        // If already an array, return as-is
        if (Array.isArray(val)) {
            return val;
        }
        // If null or undefined, return empty array
        if (val === null || val === undefined || val === '') {
            return [];
        }
        // If string, try to parse as JSON first, otherwise convert to array
        if (typeof val === 'string') {
            try {
                const parsed = JSON.parse(val);
                return Array.isArray(parsed) ? parsed : [parsed];
            } catch {
                // If not valid JSON, treat as single string value
                return [val];
            }
        }
        // For any other type, convert to array
        return [val];
    },
    z.array(z.string()).nullable().optional()
)

/**
 * Schema for claimedBy fields when sending to the API (expects JSON strings, not arrays).
 * 
 * This schema is used when validating data before sending to the backend API.
 * The backend expects JSON strings in VARCHAR fields, so this schema accepts strings
 * that are valid JSON arrays, or plain strings, but NOT actual arrays.
 * 
 * @example
 * claimedByApiSchema.parse('["Adventures Relief", "CORE"]') // ✅ Valid - JSON string
 * claimedByApiSchema.parse("Adventures Relief") // ✅ Valid - plain string
 * claimedByApiSchema.parse(null) // ✅ Valid - null
 * claimedByApiSchema.parse(["Adventures Relief"]) // ❌ Invalid - array not allowed
 * 
 * @returns Zod schema that validates strings (JSON or plain) for API requests
 */
export const claimedByApiSchema = z.preprocess(
    (val) => {
        // If it's already a string, keep it as-is (could be JSON string or plain string)
        if (typeof val === 'string') {
            return val;
        }
        // If it's an array, convert to JSON string
        if (Array.isArray(val)) {
            return val.length > 0 ? JSON.stringify(val) : null;
        }
        // If null or undefined, return null
        if (val === null || val === undefined || val === '') {
            return null;
        }
        // For any other type, convert to string
        return String(val);
    },
    z.string().nullable().optional()
)

/**
 * Schema for date range validation with logical constraints.
 * 
 * Validates that:
 * - Both 'from' and 'to' dates are provided
 * - 'from' date is before 'to' date
 * - Both dates are valid Date objects
 * 
 * Useful for date range pickers, booking systems, and time period selections.
 * 
 * @example
 * dateRangeSchema.parse({
 *   from: new Date('2023-01-01'),
 *   to: new Date('2023-01-31')
 * }) // ✅ Valid
 * 
 * dateRangeSchema.parse({
 *   from: new Date('2023-01-31'),
 *   to: new Date('2023-01-01')
 * }) // ❌ Invalid - from date after to date
 * 
 * @returns Zod schema that validates date ranges with logical constraints
 */
export const dateRangeSchema = z.object({
    from: z.date({message: "From date is required"}),
    to: z.date({message: "To date is required"}),
}).refine((data) => {
    return data.from < data.to
}, {
    message: "From date must be before to date",
})